import { useEffect, useRef, useState } from 'react';

import type { LoadingStateType } from '@/shared/config';
import {
  assembleFullHeadMprReportTiles,
  makeFullHeadMprReportViewRequests,
  renderFullHeadMPRView,
  resolveFullHeadMprViewRasterSize,
  type FullHeadMPRSavedView,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { debug } from '@/shared/lib';

import type {
  FullHeadMprSliceGroupView,
  FullHeadMprSliceRecordViewsByTileID,
} from '../config';
import {
  getFullHeadMprSavedViewTiles,
  pixelsToPngUrl,
  putFullHeadMprSavedViewTiles,
} from '../lib';

const makeRasterKey = ({
  toothID,
  view,
  thicknessMm,
}: {
  toothID: string;
  view: FullHeadMPRSavedView;
  thicknessMm?: number;
}) => `${toothID}:${thicknessMm ?? 'saved'}:${JSON.stringify(view)}`;

export const useFullHeadMprReportTiles = ({
  groups,
  isEnabled = true,
  recordsByTileID,
  toothID,
}: {
  groups: FullHeadMprSliceGroupView[];
  recordsByTileID: FullHeadMprSliceRecordViewsByTileID;
  toothID: string;
  isEnabled?: boolean;
}) => {
  const requests = makeFullHeadMprReportViewRequests({
    groups,
    recordsByTileID,
  });
  const key = `${toothID}:${JSON.stringify(requests)}`;
  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  const cacheRef = useRef(getFullHeadMprSavedViewTiles(toothID));
  const [rendered, setRendered] = useState<{
    key: string;
    loading: LoadingStateType;
  }>({ key: '', loading: 'idle' });

  useEffect(() => {
    const requests = requestsRef.current;
    cacheRef.current = getFullHeadMprSavedViewTiles(toothID);
    if (!isEnabled || !requests.length) {
      cacheRef.current = {};
      return;
    }
    cacheRef.current = Object.fromEntries(
      Object.entries(cacheRef.current).filter(([tileID]) =>
        requests.some((request) => request.id === tileID),
      ),
    );
    const controller = new AbortController();
    setRendered({ key, loading: 'pending' });
    const render = async () => {
      await requests.reduce<Promise<void>>(async (previous, request) => {
        await previous;
        controller.signal.throwIfAborted();
        const requestKey = makeRasterKey({ toothID, ...request });
        const cached = cacheRef.current[request.id];
        if (cached?.key === requestKey) {
          return;
        }
        const raster = await renderFullHeadMPRView({
          ...resolveFullHeadMprViewRasterSize(request.view),
          signal: controller.signal,
          thicknessMm: request.thicknessMm,
          view: request.view,
        });
        controller.signal.throwIfAborted();
        cacheRef.current = {
          ...cacheRef.current,
          [request.id]: {
            key: requestKey,
            pngUrl: pixelsToPngUrl(raster),
            view: raster.view,
          },
        };
        putFullHeadMprSavedViewTiles(toothID, cacheRef.current);
      }, Promise.resolve());
      if (!controller.signal.aborted) {
        setRendered({ key, loading: 'succeeded' });
      }
    };
    void render().catch((error: unknown) => {
      if (!controller.signal.aborted) {
        debug(
          `[FullHeadMPR] event=saved_view_render_failed message=${error instanceof Error ? error.message : String(error)}`,
        );
        setRendered({ key, loading: 'failed' });
      }
    });
    return () => controller.abort();
  }, [key, isEnabled, toothID]);

  const renderedByID = Object.fromEntries(
    requests.flatMap((request) => {
      const cached = cacheRef.current[request.id];

      return isEnabled && cached?.key === makeRasterKey({ toothID, ...request })
        ? [[request.id, { pngUrl: cached.pngUrl, view: cached.view }] as const]
        : [];
    }),
  );
  const assembled = assembleFullHeadMprReportTiles({
    groups,
    renderedByID,
    requests,
    toothID,
  });
  return {
    groups: assembled.groups,
    hasSavedViews: requests.length > 0,
    savedViewsLoading:
      !requests.length || assembled.savedTiles.length === requests.length
        ? ('succeeded' as const)
        : rendered.key === key
          ? rendered.loading
          : ('pending' as const),
    tiles: assembled.tiles,
  };
};
